var fileDownload = require("js-file-download");

import Vue from "vue";
const vm = Vue.prototype;

import $i18n from "../i18n";

import checkApp from "../service/check-application.js";

// ─────────────────────────────────────────────────────────────────────────────
// TAB ROUTE TABLE — three POSITIONAL, INDEX-ALIGNED arrays.
//
// statuses[i] / filterStatuses[i] / exportFilterStatuses[i] all describe the SAME
// tab i (see the <v-tabs> strip in views/admin/admin-applications.vue, which must
// have exactly this many tabs in exactly this order).
//
// Adding or removing an entry MUST be done in all three arrays at the same index.
// Editing only some of them fails SILENTLY rather than loudly: every lookup below
// is written `arr[i] || arr[0]`, so a missing entry falls back to the JAMI/"all"
// route and the new tab just quietly renders the unfiltered list.
// ─────────────────────────────────────────────────────────────────────────────
const statuses = [
  "",
  "/completed",
  "/dtm",
  "/magistr",
  "/second-spes",
  "/submitted",
  "/rejected",
  "/accepted",
  "/passed",
  "/examined",
  "/examined-passed",
  "/examined-failed",
  "/bitrix-failed",
  "/excel-imported",
];

const filterStatuses = [
  "/filter",
  "/filter/completed",
  "/filter/dtm",
  "/filter/magistr",
  "/filter/second-spes",
  "/filter/submitted",
  "/filter/rejected",
  "/filter/accepted",
  "/filter/passed",
  "/filter/examined",
  "/filter/examined-passed",
  "/filter/examined-failed",
  "/filter/bitrix-failed",
  "/filter/excel-imported",
];

const exportFilterStatuses = [
  "/export/filter",
  "/export/filter/completed",
  "/export/filter/dtm",
  "/export/filter/magistr",
  "/export/filter/second-spes",
  "/export/filter/submitted",
  "/export/filter/rejected",
  "/export/filter/accepted",
  "/export/filter/passed",
  "/export/filter/examined",
  "/export/filter/examined-passed",
  "/export/filter/examined-failed",
  "/export/filter/bitrix-failed",
  "/export/filter/excel-imported",
];

export const state = () => ({
  applications: [],
  count: [],
  growth: [],
  application: checkApp(),
});

export const mutations = {
  SET_APPLICATIONS(state, payload) {
    state.applications = vm.$toArray(payload);
  },

  SET_COUNT(state, payload) {
    state.count = payload || {};
  },

  SET_GROWTH(state, payload) {
    state.growth = payload || [];
  },

  SET_BY_ID(state, payload) {
    state.application = checkApp(payload);
  },
  
  MOVE_TO_2025(state, payload) {
    state.payments = vm.$toArray(payload);
  },

  MOVE_ALL_TO_2025(state, payload) {
    state.payments = vm.$toArray(payload);
  },
};

export const actions = {
  async INIT_APPLICATIONS({ commit }, options) {
    const { itemsPerPage, search = "", sortBy = [], sortDesc = [] } = options;

    const page = Number(options.page - 1) || 0;
    const sortField = sortBy.length ? sortBy[0] : "";
    const sortOrder = sortDesc.length && sortDesc[0] ? "desc" : "asc";

    const senc = search ? encodeURI(search) : null;

    const query = `?page=${page}&size=${itemsPerPage}&name=${senc}&sortField=${sortField}&sortOrder=${sortOrder}`;
    let totalItemsCount = 0;

    const status = statuses[options.status] || "";

    const data = await vm.$axios.$get(`admin/application${status}${query}`);

    if (data) {
      commit("SET_APPLICATIONS", data.content);

      totalItemsCount = data.totalElements;
    }

    return totalItemsCount;
  },

  // Resolve ALL application ids for the current view (tab + filters + search),
  // using the SAME canonical status arrays as INIT_APPLICATIONS so "select all"
  // bulk actions can never target a different tab's rows.
  async FETCH_VIEW_IDS(_ctx, { status, isFiltering, filters, search, size }) {
    const senc = search ? encodeURIComponent(search) : null;
    const total = Number(size) || 999999;
    const query = `?page=0&size=${total}&name=${senc}&sortField=&sortOrder=asc`;
    let data;
    if (isFiltering) {
      const path = filterStatuses[status] || filterStatuses[0];
      data = await vm.$axios.$post(`admin/application${path}${query}`, filters);
    } else {
      const path = statuses[status] || "";
      data = await vm.$axios.$get(`admin/application${path}${query}`);
    }
    return ((data && data.content) || []).map((it) => it.id);
  },

  async INIT_APPLICATIONS_PAID({ commit }, options) {
    const { itemsPerPage, search = "", sortBy = [], sortDesc = [] } = options;

    const page = Number(options.page - 1) || 0;
    const sortField = sortBy.length ? sortBy[0] : "";
    const sortOrder = sortDesc.length && sortDesc[0] ? "desc" : "asc";

    const senc = search ? encodeURI(search) : null;

    const query = `?page=${page}&size=${itemsPerPage}&name=${senc}&sortField=${sortField}&sortOrder=${sortOrder}`;
    let totalItemsCount = 0;

    const status = "/passed";

    const data = await vm.$axios.$get(`admin/application${status}${query}`);

    if (data) {
      commit("SET_APPLICATIONS", data.content);

      totalItemsCount = data.totalElements;
    }

    return totalItemsCount;
  },

  async INIT_FILTERED_APPLICATIONS({ commit }, options) {
    const { itemsPerPage, sortBy = [], sortDesc = [] } = options;

    const page = Number(options.page - 1) || 0;
    const sortField = sortBy.length ? sortBy[0] : "";
    const sortOrder = sortDesc.length && sortDesc[0] ? "desc" : "asc";

    const query = `?page=${page}&size=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;
    let totalItemsCount = 0;

    const status = filterStatuses[options.status] || filterStatuses[0];

    const data = await vm.$axios.$post(`admin/application${status}${query}`, options.filters || {});

    if (data) {
      commit("SET_APPLICATIONS", data.content);
      totalItemsCount = data.totalElements;
    }

    return totalItemsCount;
  },

  async EXPORT_EXCEL_BY_FILTER(ctx, options) {
    const status = exportFilterStatuses[options.status] || exportFilterStatuses[0];

    const res = await vm.$axios.$postBlob2(`admin/application${status}`, options.filters || {});

    if (res?.data) {
      const ss = `${status || ""}`.replace(/\//g, "_");
      const file_name = `filtered${ss || "_all"}_application`;
      const date = new Date().toISOString().substr(0, 10);
      fileDownload(res.data, `${file_name}_${date}.xlsx`);
    }

    return true;
  },

  async EXPORT_EXCEL(ctx, options) {
    const status = statuses[options.status] || "";

    const res = await vm.$axios.$getBlob2(`admin/application/export${status}`);

    if (res?.data) {
      const ss = `${status || ""}`.replace("/", "");
      const file_name = `${ss?.trim() ? ss : "all"}_application`;
      const date = new Date().toISOString().substr(0, 10);
      fileDownload(res.data, `${file_name}_${date}.xlsx`);
    }

    return true;
  },

  async EXPORT_EXCEL_PAID() {
    const status = "/passed";

    const res = await vm.$axios.$getBlob2(`admin/application/export${status}`);

    if (res?.data) {
      const ss = `${status || ""}`.replace("/", "");
      const file_name = `${ss?.trim() ? ss : "all"}_application`;
      const date = new Date().toISOString().substr(0, 10);
      fileDownload(res.data, `${file_name}_${date}.xlsx`);
    }

    return true;
  },

  async EXPORT_EXAM() {
    const res = await vm.$axios.$getBlob2(`admin/application/export/exported`);

    if (res?.data) {
      const file_name = `exam_applications`;
      const date = new Date().toISOString().substr(0, 10);
      fileDownload(res.data, `${file_name}_${date}.xlsx`);
    }

    return true;
  },

  async EXPORT_RESULT() {
    const res = await vm.$axios.$getBlob2(`admin/contract2023/export/exported`);

    if (res?.data) {
      const file_name = `usatga_kirgan_arizalar`;
      const date = new Date().toISOString().substr(0, 10);
      fileDownload(res.data, `${file_name}_${date}.xlsx`);
    }
    return true;
  },

  async INIT_COUNT({ commit }) {
    const data = await vm.$axios.$get(`admin/application/count`);
    commit("SET_COUNT", data);
    return data != null;
  },

  async INIT_GROWTH({ commit }, { days = 30 } = {}) {
    const data = await vm.$axios.$get(`admin/application/growth?days=${days}`);
    commit("SET_GROWTH", data);
    return data;
  },

  async ACCEPT_APPLICATION({ dispatch }, { id }) {
    if (id) {
      const res = await vm.$axios.$post(`admin/application/${id}/acceptance`);

      const status = res != null;
      if (status) {
        dispatch(
          "SET_SNACKBAR",
          {
            text: $i18n.t("Success"),
            color: "success",
          },
          { root: true }
        );
      }
      return status;
    } else return null;
  },

  async BULK_ACCEPT_APPLICATIONS({ dispatch }, { ids }) {
    if (ids && ids.length) {
      const res = await vm.$axios.$post(`admin/application/bulk-acceptance`, ids);

      const status = res != null;
      if (status) {
        dispatch(
          "SET_SNACKBAR",
          {
            text: $i18n.t("Success"),
            color: "success",
          },
          { root: true }
        );
      }
      return status;
    } else return null;
  },

  async PASSED_APPLICATION({ dispatch }, { id }) {
    if (id) {
      const res = await vm.$axios.$post(`admin/application/${id}/passed`);

      const status = res != null;
      if (status) {
        dispatch(
          "SET_SNACKBAR",
          {
            text: $i18n.t("Success"),
            color: "success",
          },
          { root: true }
        );
      }
      return status;
    } else return null;
  },

  async SUGGESTED_APPLICATION({ dispatch }, { id }) {
    if (id) {
      const res = await vm.$axios.$post(`admin/application/${id}/suggested`);

      const status = res != null;
      if (status) {
        dispatch(
          "SET_SNACKBAR",
          {
            text: $i18n.t("Success"),
            color: "success",
          },
          { root: true }
        );
      }
      return status;
    } else return null;
  },

  async FAILED_APPLICATION({ dispatch }, { id }) {
    if (id) {
      const res = await vm.$axios.$post(`admin/application/${id}/failed`);

      const status = res != null;
      if (status) {
        dispatch(
          "SET_SNACKBAR",
          {
            text: $i18n.t("Success"),
            color: "success",
          },
          { root: true }
        );
      }
      return status;
    } else return null;
  },

  async PAID_APPLICATION({ dispatch }, { id }) {
    if (id) {
      const res = await vm.$axios.$post(`admin/contract2024/create/${id}`);

      const status = res != null;
      if (status) {
        dispatch(
          "SET_SNACKBAR",
          {
            text: $i18n.t("Success"),
            color: "success",
          },
          { root: true }
        );
      }
      return status;
    } else return null;
  },

  async REJECT_APPLICATION({ dispatch }, { id, reviews }) {
    if (id) {
      const res = await vm.$axios.$post(
        `admin/application/${id}/rejects`,
        reviews
      );

      const status = res != null;
      if (status) {
        dispatch(
          "SET_SNACKBAR",
          {
            text: $i18n.t("Success"),
            color: "success",
          },
          { root: true }
        );
      }
      return status;
    } else return null;
  },

  async UPDATE_APPLICATION({ commit, dispatch }, { id, data }) {
    if (!id) return false;
    const res = await vm.$axios.$put(`admin/applications/${id}`, data);
    const status = res != null;
    if (status) {
      commit("SET_BY_ID", res);
      dispatch("SET_SNACKBAR", { text: $i18n.t("Success"), color: "success" }, { root: true });
    }
    return status;
  },

  async DELETE_APPLICATION({ dispatch }, { id }) {
    if (id) {
      const res = await vm.$axios.$delete(`admin/application/${id}`);

      const status = res != null;
      if (status) {
        dispatch(
          "SET_SNACKBAR",
          {
            text: $i18n.t("Success"),
            color: "success",
          },
          { root: true }
        );
      }
      return status;
    } else return null;
  },

  async DELETE_APPLICATIONS_BY_IDS({ dispatch }, ids) {
    if (!ids || !ids.length) return false;
    try {
      await vm.$axios.$delete(`admin/application/by-ids`, { data: ids });
      dispatch("SET_SNACKBAR", { text: $i18n.t("Success"), color: "success" }, { root: true });
      return true;
    } catch (e) {
      // Surface the REAL server reason (e.g. the FK/constraint that blocks the delete) instead of a
      // generic "Error" — the backend no longer swallows the failure.
      const msg = e?.response?.data?.error_description || $i18n.t("Error");
      dispatch("SET_SNACKBAR", { text: msg, color: "error" }, { root: true });
      return false;
    }
  },

  // Re-send selected applications to Bitrix (server skips referral-sourced + already-synced members).
  async RESEND_BITRIX_BY_IDS({ dispatch }, ids) {
    if (!ids || !ids.length) return false;
    try {
      const res = (await vm.$axios.post(`admin/application/resend-bitrix`, ids)).data;
      dispatch(
        "SET_SNACKBAR",
        { text: $i18n.t("Qayta yuborildi") + ": " + (res?.resent ?? 0), color: "success" },
        { root: true }
      );
      return res;
    } catch (e) {
      dispatch("SET_SNACKBAR", { text: $i18n.t("Error"), color: "error" }, { root: true });
      return false;
    }
  },

  async INIT_BY_ID({ commit }, { id }) {
    if (id) {
      const data = await vm.$axios.$get(`admin/application/${id}`);
      if (data) data.id = Number(id);

      commit("SET_BY_ID", data);
      return data != null;
    }
  },

  async MOVE_TO_2025(_, id) {
    const url = `/admin/contract2022/move/2025?id=${id}`;
    // const params = { id };

    const result = await vm.$axios.$post(url);
    return result;
  },

  async MOVE_ALL_TO_2025() {
    const url = `/admin/contract2022/move/2025/all`;

    const result = await vm.$axios.$post(url);
    return result;
  },
  
  async CREATE_APPLICATION({ dispatch }, applicationData) {
    const res = await vm.$axios.$post(`admin/application/create`, applicationData);

    if (res != null) {
      dispatch(
        "SET_SNACKBAR",
        {
          text: $i18n.t("Success"),
          color: "success",
        },
        { root: true }
      );
    }
    return res;
  },

  async UPLOAD_LEADS({ dispatch }, { file, sourceId }) {
    const formData = new FormData();
    formData.append("file", file);
    if (sourceId) formData.append("sourceId", sourceId);
    // The $post helper swallows errors and returns null, so call axios
    // directly here — we need to know if the upload actually failed.
    try {
      const res = (await vm.$axios.post(`admin/application/upload-leads`, formData)).data;
      const created = res?.created ?? 0;
      const dup = res?.duplicates ?? 0;
      const resynced = res?.resynced ?? 0;
      dispatch(
        "SET_SNACKBAR",
        {
          text: $i18n.t("Yaratildi") + ": " + created + " | " + $i18n.t("Takroriy") + ": " + dup +
            (resynced ? " | " + $i18n.t("Qayta yuborildi") + ": " + resynced : ""),
          color: "success",
        },
        { root: true }
      );
      return res;
    } catch (e) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.message || e?.message || "";
      dispatch(
        "SET_SNACKBAR",
        {
          text: $i18n.t("Upload failed") + (status ? " (" + status + ")" : "") + (serverMsg ? ": " + serverMsg : ""),
          color: "error",
        },
        { root: true }
      );
      throw e;
    }
  },

  async DELETE_ALL({ dispatch }) {
    const res = await vm.$axios.$delete(`admin/contract2022/delete/all`);

    const status = res != null;
    if (status) {
      dispatch(
        "SET_SNACKBAR",
        {
          text: $i18n.t("Success"),
          color: "success",
        },
        { root: true }
      );
    }
    return status;
  },

  async DROP_ALL({ dispatch }) {
    try {
      await vm.$axios.$put(`admin/contract2022/dropout/all`);
      dispatch("SET_SNACKBAR", { text: $i18n.t("Success"), color: "success" }, { root: true });
      return true;
    } catch (e) {
      dispatch("SET_SNACKBAR", { text: $i18n.t("Error"), color: "error" }, { root: true });
      return false;
    }
  },

  async FETCH_FILTERED_IDS(_, { filters, status, search }) {
    const statusStr = statuses[status] || "";
    const senc = search ? encodeURI(search) : null;
    const query = `?page=0&size=999999&name=${senc}&sortField=&sortOrder=asc`;
    const url = filters
      ? `admin/contract2022/filter${statusStr}${query}`
      : `admin/contract2022${statusStr}${query}`;
    const res = filters
      ? await vm.$axios.$post(url, filters)
      : await vm.$axios.$get(url);
    return (res?.content || []).map((c) => c.id);
  },

  async DROP_BY_IDS(_, ids) {
    // Caller (AdminApplicationsPaidList) owns the snackbar so the message
    // matches the UI action; don't fire a generic Success/Error here.
    if (!ids || !ids.length) return false;
    try {
      await vm.$axios.$put(`admin/contract2022/dropout/by-ids`, ids);
      return true;
    } catch (e) {
      return false;
    }
  },

  async DELETE_BY_IDS({ dispatch }, ids) {
    if (!ids || !ids.length) return false;
    try {
      await vm.$axios.$delete(`admin/contract2022/by-ids`, { data: ids });
      dispatch("SET_SNACKBAR", { text: $i18n.t("Success"), color: "success" }, { root: true });
      return true;
    } catch (e) {
      dispatch("SET_SNACKBAR", { text: $i18n.t("Error"), color: "error" }, { root: true });
      return false;
    }
  },

  async RESTORE_BY_IDS({ dispatch }, ids) {
    if (!ids || !ids.length) return false;
    try {
      await vm.$axios.$put(`admin/contract2022/dropout/restore/by-ids`, ids);
      dispatch("SET_SNACKBAR", { text: $i18n.t("Success"), color: "success" }, { root: true });
      return true;
    } catch (e) {
      dispatch("SET_SNACKBAR", { text: $i18n.t("Error"), color: "error" }, { root: true });
      return false;
    }
  },
};

export const getters = {
  GET_APPLICATIONS: (state) => state.applications,
  GET_COUNT: (state) => state.count,
  GET_GROWTH: (state) => state.growth,
  GET_APPLICATION: (state) => state.application,
};
